import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CreditCard, Upload, FileText, Trash2, Download, Check, ChevronDown, ChevronUp, ChevronRight, AlertTriangle, Info, Package, ExternalLink, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const MONTH_NAMES = ['januari','februari','maart','april','mei','juni','juli','augustus','september','oktober','november','december'];

const PLEO_1482 = {
  "TW1 Aalsmeer BV":"1482AALS","TW1 Amsterdam BV":"1482AMST","TW1 Asten BV":"1482ASTE",
  "TW1 Bodegraven BV":"1482BODE","TW1 Ede BV":"1482EDE","TW1 Eindhoven BV":"1482EIND",
  "TW1 Groenlo BV":"1482GROE","TW1 Holten BV":"1482HOLT","TW1 Maasvlakte BV":"1482MAAS",
  "TW1 Rilland BV":"1482RILL","TW1 Roosendaal BV":"1482ROOS","TW1 Rotterdam BV":"1482ROTT",
  "TW1 Steenwijk BV":"1482STEE","TW1 Utrecht BV":"1482UTRE","TW1 Venlo BV":"1482VENL",
  "TW1 Wehl BV":"1482WEHL","TW1 Hazeldonk BV":"1482HAZE","Truckstop 8 BV":"1482ACHT",
};

const EXTRA_ENTITIES = ["Techniek en Beheer BV", "Truckshop 1 BV"];

function lastDay(year, month) {
  return new Date(year, month, 0).toLocaleDateString('nl-NL', { day:'2-digit', month:'2-digit', year:'numeric' }).split('/').join('-');
}

function lastDayISO(year, month) {
  const d = new Date(year, month, 0);
  return `${d.getDate().toString().padStart(2,'0')}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getFullYear()}`;
}

function formatEUR(v) {
  return new Intl.NumberFormat('nl-NL', { style:'currency', currency:'EUR' }).format(v || 0);
}

function downloadXls(rows, headers, filename, sheetName) {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws['!cols'] = headers.map(() => ({ wch: 20 }));
  XLSX.utils.book_append_sheet(wb, ws, sheetName || 'Blad1');
  const b64 = XLSX.write(wb, { bookType: 'xls', type: 'base64' });
  const a = document.createElement('a');
  a.href = `data:application/vnd.ms-excel;base64,${b64}`;
  a.download = filename;
  a.click();
  return b64;
}

function FileDropzone({ label, description, file, onFileChange, onRemove, accept, inputRef, testId }) {
  const [dragOver, setDragOver] = useState(false);
  const handleDrop = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); if (e.dataTransfer.files?.[0]) onFileChange(e.dataTransfer.files[0]); }, [onFileChange]);
  const handleInput = useCallback((e) => { if (e.target.files?.[0]) onFileChange(e.target.files[0]); if (inputRef?.current) inputRef.current.value=''; }, [onFileChange, inputRef]);
  if (file) {
    return (
      <div className="tw1-card p-4" data-testid={testId}>
        <div className="flex items-start justify-between mb-2">
          <div><p className="text-sm font-semibold text-[#0b1220]">{label}</p><p className="text-[10px] text-[#526071] mt-0.5">{description}</p></div>
          <Badge variant="outline" className="bg-[#1e8c45]/8 text-[#1e8c45] border-[#1e8c45]/20 text-[10px] font-semibold">Geladen</Badge>
        </div>
        <div className="flex items-center justify-between bg-[#1e8c45]/5 rounded-xl px-3 py-2.5 border border-[#1e8c45]/10">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-[#1e8c45]/10 flex items-center justify-center flex-shrink-0"><FileText className="w-3.5 h-3.5 text-[#1e8c45]" /></div>
            <div className="min-w-0"><p className="text-xs font-medium truncate">{file.name}</p><p className="text-[10px] text-[#526071]">{(file.size / 1024).toFixed(1)} KB</p></div>
          </div>
          <button onClick={onRemove} className="text-[#526071] hover:text-[#c0392b] transition-colors p-1"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    );
  }
  return (
    <div className="tw1-card p-4" data-testid={testId}>
      <p className="text-sm font-semibold text-[#0b1220] mb-0.5">{label}</p>
      <p className="text-[10px] text-[#526071] mb-2.5">{description}</p>
      <label onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
        className={`flex flex-col items-center justify-center gap-1.5 px-3 py-5 border-2 border-dashed rounded-xl cursor-pointer transition-all ${dragOver ? 'border-[#f59e0b] bg-[#f59e0b]/8' : 'border-[#dde3ee] hover:border-[#f59e0b] hover:bg-[#f59e0b]/4'}`}>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${dragOver ? 'bg-[#f59e0b]/15' : 'bg-[#003366]/5'}`}>
          <Upload className={`w-4 h-4 ${dragOver ? 'text-[#f59e0b]' : 'text-[#003366]'}`} />
        </div>
        <div className="text-center"><span className="text-xs font-medium text-[#0b1220]">Sleep hierheen</span><span className="text-[10px] text-[#526071] block mt-0.5">of klik om te kiezen</span></div>
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handleInput} />
      </label>
    </div>
  );
}

export default function PleoDoorbelastingPage() {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [extraCodes, setExtraCodes] = useState({});
  const [expanded, setExpanded] = useState({});
  const [exporting, setExporting] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/api/pleo/settings').then(r => setExtraCodes(r.data.extra_codes || {})).catch(() => {});
  }, []);

  const saveExtraCodes = async (codes) => {
    setExtraCodes(codes);
    try { await api.put('/api/pleo/settings', { extra_codes: codes }); toast.success('1482-codes opgeslagen'); } catch { toast.error('Opslaan mislukt'); }
  };

  const handleParse = async () => {
    if (!file) { toast.error('Upload een Excel bestand'); return; }
    setLoading(true); setData(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/pleo/parse', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      if (res.data.error) { toast.error(res.data.error); return; }
      setData(res.data);
      setStep(1);
      toast.success(`${res.data.transacties} transacties verwerkt`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Verwerking mislukt'); }
    finally { setLoading(false); }
  };

  const get1482 = (entity) => PLEO_1482[entity] || extraCodes[entity] || null;

  const entities = data ? Object.values(data.entities).filter(e => e.entity !== '__GROUP__').sort((a,b) => a.entity.localeCompare(b.entity)) : [];
  const groupEntity = data?.entities?.['__GROUP__'] || null;
  const periodeLabel = data?.periodes?.length > 0 ? data.periodes.map(p => MONTH_NAMES[(p-1)] || `P${p}`).join(', ') : '—';
  const periodeFileLabel = data?.periodes?.length > 1 ? `P${data.periodes[0]}-P${data.periodes[data.periodes.length-1]}` : data?.periodes?.length === 1 ? `P${data.periodes[0]}` : '';
  const boekjaar = data?.boekjaar || new Date().getFullYear();

  // Validation
  const missingCodes = entities.filter(e => !get1482(e.entity)).map(e => e.entity);
  const groupCreditTotal = entities.reduce((s, e) => s + e.totaal, 0);
  const holdingTotal = entities.reduce((s, e) => {
    return s + Object.values(e.periodes).reduce((ps, p) => ps + p.subtotaal, 0);
  }, 0);
  const entityDebitTotal = entities.reduce((s, e) => s + e.totaal, 0);
  const balanceOk = data ? (Math.abs(groupCreditTotal - holdingTotal) < 0.01 && Math.abs(holdingTotal - entityDebitTotal) < 0.01 && Math.abs(groupCreditTotal - data.totaal_doorbelasten) < 0.01) : false;

  const handleExport = async () => {
    if (missingCodes.length > 0) { toast.error(`1482-codes ontbreken voor: ${missingCodes.join(', ')}`); return; }
    setExporting(true);
    try {
      const H_GROUP = ['Dagboek','Boekjaar','Periode','Factuurdatum','Grootboekrekening','Omschrijving','Btw-code','Bedrag','Aantal','Kostenplaats'];
      const H_HOLDING = ['Relatienummer','Dagboek','Boekjaar','Periode','Factuurdatum','Omschrijving','Grootboekrekening','Omschrijving2','Btw-code','Bedrag','Aantal'];
      const H_ENTITY = ['Dagboek','Boekjaar','Periode','Factuurdatum','Grootboekrekening','Omschrijving','Btw-code','Bedrag','Aantal'];

      // GROUP export (dagboek 90 — Memoriaal IC Correctie)
      const groupRows = [];
      for (const ent of entities) {
        for (const p of data.periodes) {
          const pData = ent.periodes[String(p)];
          if (!pData) continue;
          const fd = lastDayISO(boekjaar, p);
          for (const r of pData.regels) {
            const omschr = `Pleo P${p} ${boekjaar} - ${ent.entity} - ${r.gb_code} ${r.gb_naam}`;
            // Credit kostenrekening (negatief = haalt kosten weg bij Group)
            groupRows.push(['90', String(boekjaar), String(p), fd, r.gb_code, omschr, '', Math.round(-r.netto * 100) / 100, 1, 'TW1G']);
            // Debit 1482HOLD (positief = vordering op Holding)
            groupRows.push(['90', String(boekjaar), String(p), fd, '1482HOLD', omschr, '', Math.round(r.netto * 100) / 100, 1, 'TW1G']);
          }
        }
      }

      // HOLDING export
      const holdingRows = [];
      for (const ent of entities) {
        for (const p of data.periodes) {
          const pData = ent.periodes[String(p)];
          if (!pData || pData.subtotaal === 0) continue;
          const fd = lastDayISO(boekjaar, p);
          const code1482 = get1482(ent.entity);
          const omschr = `Pleo P${p} ${boekjaar} - ${ent.entity}`;
          // 1482[LOC] negatief (Holding belast door aan locatie)
          holdingRows.push(['2', '60', String(boekjaar), String(p), fd, omschr, code1482, `Doorbelasting ${ent.entity}`, '01', Math.round(-pData.subtotaal * 100) / 100, 1]);
          // 1482GROU positief (Holding schuld aan Group)
          holdingRows.push(['2', '60', String(boekjaar), String(p), fd, omschr, '1482GROU', `Doorbelasting ${ent.entity}`, '01', Math.round(pData.subtotaal * 100) / 100, 1]);
        }
      }

      // PER ENTITY exports
      const entityFiles = {};
      for (const ent of entities) {
        const rows = [];
        for (const p of data.periodes) {
          const pData = ent.periodes[String(p)];
          if (!pData) continue;
          const fd = lastDayISO(boekjaar, p);
          for (const r of pData.regels) {
            const omschr = `Pleo P${p} ${boekjaar} - ${r.gb_code} ${r.gb_naam}`;
            // Debit kostenrekening (positief = kosten bij entiteit)
            rows.push(['90', String(boekjaar), String(p), fd, r.gb_code, omschr, '', Math.round(r.netto * 100) / 100, 1]);
            // Credit 1482GROU (negatief = schuld aan Group via Holding)
            rows.push(['90', String(boekjaar), String(p), fd, '1482GROU', omschr, '', Math.round(-r.netto * 100) / 100, 1]);
          }
        }
        if (rows.length > 0) {
          const safeName = ent.entity.replace(/[^a-zA-Z0-9]/g, '_');
          entityFiles[safeName] = { entity: ent.entity, rows, headers: H_ENTITY };
        }
      }

      // Download Group
      const gFn = `Pleo_IC_Group_${periodeFileLabel}_${boekjaar}.xls`;
      downloadXls(groupRows, H_GROUP, gFn, 'Group IC Correctie');

      // Download Holding
      await new Promise(r => setTimeout(r, 300));
      const hFn = `Pleo_IC_Holding_${periodeFileLabel}_${boekjaar}.xls`;
      downloadXls(holdingRows, H_HOLDING, hFn, 'Holding IC Correctie');

      // Download per entity
      let count = 2;
      for (const [safeName, ef] of Object.entries(entityFiles)) {
        await new Promise(r => setTimeout(r, 300));
        const eFn = `Pleo_IC_${safeName}_${periodeFileLabel}_${boekjaar}.xls`;
        downloadXls(ef.rows, ef.headers, eFn, 'Entiteit IC Correctie');
        count++;
      }

      // Log export
      api.post('/api/pleo/export', { filename: `Pleo_IC_${periodeFileLabel}_${boekjaar}.zip`, metadata: { entiteiten: entities.length, periodes: data.periodes, regels: groupRows.length + holdingRows.length } }).catch(() => {});

      toast.success(`${count} bestanden gedownload`);
    } catch (err) { toast.error('Export mislukt: ' + (err.message || '')); }
    finally { setExporting(false); }
  };

  const clearAll = () => { setFile(null); setData(null); setStep(0); setExpanded({}); if (fileRef.current) fileRef.current.value = ''; };

  return (
    <div className="tw1-fade-in" data-testid="pleo-page">
      {/* Hero Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#003366] to-[#00244d] p-6 mb-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#f59e0b]/10 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="relative flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#f59e0b]/20 flex items-center justify-center">
            <CreditCard className="w-6 h-6 text-[#f59e0b]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Pleo Doorbelasting</h1>
            <p className="text-sm text-white/60">Pleo kosten doorbelasten van Group naar locaties via Holding</p>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6">
        {[{ key:'upload', label:'Upload & Parse' }, { key:'overzicht', label:'Overzicht' }, { key:'export', label:'Exporteren' }].map((s, i) => (
          <React.Fragment key={s.key}>
            <button onClick={() => { if (i <= step || (i === 1 && data) || (i === 2 && data)) setStep(i); }}
              disabled={i > 0 && !data}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${step === i ? 'bg-[#003366] text-white shadow-sm' : step > i ? 'bg-[#1e8c45]/10 text-[#1e8c45]' : 'bg-gray-100 text-[#526071]'} ${i > 0 && !data ? 'opacity-40 cursor-not-allowed' : ''}`}
              data-testid={`pleo-step-${s.key}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === i ? 'bg-white/20' : step > i ? 'bg-[#1e8c45]/20' : 'bg-gray-200'}`}>{step > i ? <Check className="w-3 h-3" /> : i + 1}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </button>
            {i < 2 && <ChevronRight className="w-4 h-4 text-gray-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 0: Upload */}
      {step === 0 && (
        <div className="space-y-5">
          {/* Handleiding toggle */}
          <button onClick={() => setShowGuide(!showGuide)} className="flex items-center gap-2 text-xs text-[#003366] hover:text-[#00244d] font-medium transition-colors" data-testid="pleo-guide-toggle">
            <BookOpen className="w-4 h-4" />
            <span>Handleiding: Export uit Exact & Importeren</span>
            {showGuide ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {/* Handleiding cards */}
          {showGuide && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Card 1: Export uit Exact */}
              <div className="tw1-card p-5" data-testid="pleo-guide-export">
                <h3 className="font-semibold text-sm text-[#0b1220] mb-3 flex items-center gap-2">
                  <Download className="w-4 h-4 text-[#003366]" /> Export uit Exact Online
                </h3>
                <ol className="space-y-2 text-xs text-[#526071] list-decimal list-inside">
                  <li>Ga naar <strong>Exact Online</strong> → <strong>Truckwash 1 Group B.V. (36555)</strong></li>
                  <li>Financieel → Rapporten → <strong>Grootboekkaarten</strong></li>
                  <li>Bestandstype: <strong>Excel</strong></li>
                  <li>Grootboekrekening: <strong className="font-mono text-[#003366]">4018 t/m 4099</strong></li>
                  <li>Selectie: <strong>Periode</strong></li>
                  <li>Boekjaar + Periode instellen (bijv. <strong>1-3</strong> voor jan t/m mrt)</li>
                  <li>Grootboekrekeningen: <strong>Alleen met mutaties</strong></li>
                  <li>Na boeken: <strong>aanvinken</strong></li>
                  <li>Uitsluiten Correctieboekingen: <strong className="text-[#c0392b]">NIET aanvinken</strong></li>
                  <li>Transacties: <strong>Niet afgeletterd</strong></li>
                  <li>Kostenanalyse: <strong>Code + Omschrijving</strong></li>
                  <li>Alle overige: <strong>Nee</strong></li>
                  <li>Klik op <strong>Genereren</strong> → download Excel</li>
                </ol>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <a href="https://start.exactonline.nl" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#003366] font-medium hover:underline">
                    <ExternalLink className="w-3 h-3" /> Open Exact Online
                  </a>
                </div>
              </div>

              {/* Card 2: Importeren */}
              <div className="tw1-card p-5" data-testid="pleo-guide-import">
                <h3 className="font-semibold text-sm text-[#0b1220] mb-3 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-[#003366]" /> Importeren in Exact Online
                </h3>
                <ol className="space-y-2 text-xs text-[#526071] list-decimal list-inside">
                  <li>Open de juiste entiteit (Group / Holding / locatie)</li>
                  <li>Import/Export → <strong>CSV/Excel</strong></li>
                  <li>Importtype: <strong>Memoriaalboek</strong> (Group + locaties) of <strong>Inkoopboek</strong> (Holding)</li>
                  <li>Bestandstype: <strong>Excel 97/2003</strong></li>
                  <li>Bestand kiezen → Maandafsluiting → <strong>Nee</strong></li>
                  <li>Aan veranderde waarde → <strong>3× Volgende</strong></li>
                  <li>Vakje <strong>Uitsluiten</strong> → Klaar</li>
                </ol>
                <div className="mt-3 pt-3 border-t border-gray-100 flex gap-3">
                  <a href="https://start.exactonline.nl" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#003366] font-medium hover:underline">
                    <ExternalLink className="w-3 h-3" /> Open Exact Online
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Upload section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-4">
              <FileDropzone label="Exact Online Export" description="Excel grootboekkaarten met kostenanalyse (.xlsx / .xls)" file={file} onFileChange={(f) => { setFile(f); toast.info(`Bestand: ${f.name}`); }} onRemove={clearAll} accept=".xlsx,.xls" inputRef={fileRef} testId="pleo-dropzone" />
              <div className="flex gap-2">
                <Button onClick={handleParse} disabled={loading || !file} className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white h-9 px-5" data-testid="pleo-parse-btn">
                  {loading ? (<><span className="animate-spin mr-2 w-4 h-4 border-2 border-white/30 border-t-white rounded-full inline-block" />Verwerken...</>) : (<><Package className="w-4 h-4 mr-1.5" />Verwerk bestand</>)}
                </Button>
                {file && <Button variant="outline" onClick={clearAll} className="h-9 text-xs"><Trash2 className="w-3.5 h-3.5 mr-1" />Wissen</Button>}
              </div>
            </div>
            <div className="tw1-card p-5">
              <h3 className="font-semibold text-sm text-[#0b1220] mb-3 flex items-center gap-2"><Info className="w-4 h-4 text-[#003366]" /> IC Structuur</h3>
              <div className="space-y-3 text-xs text-[#526071]">
                <div><p className="font-medium text-[#0b1220] mb-0.5">1. Group (dagboek 90)</p><p>Credit kostenrekening + Debit 1482HOLD</p><p className="text-[10px] text-[#526071]/70">Haalt kosten weg, vordering op Holding</p></div>
                <div><p className="font-medium text-[#0b1220] mb-0.5">2. Holding (dagboek 60)</p><p>1482[LOC] + 1482GROU</p><p className="text-[10px] text-[#526071]/70">Doorbelasting naar locatie, schuld aan Group</p></div>
                <div><p className="font-medium text-[#0b1220] mb-0.5">3. Per entiteit (dagboek 90)</p><p>Debit kostenrekening + Credit 1482GROU</p><p className="text-[10px] text-[#526071]/70">Kosten bij entiteit, schuld aan Group</p></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary card after parsing */}
      {data && step >= 1 && (
        <div className="tw1-card p-5 mb-6" data-testid="pleo-summary">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div><p className="text-[10px] text-[#526071] mb-1">Periodes</p><p className="text-sm font-bold text-[#0b1220]" data-testid="pleo-periodes">{periodeLabel} {boekjaar}</p></div>
            <div><p className="text-[10px] text-[#526071] mb-1">Door te belasten</p><p className="text-sm font-bold font-mono text-[#003366]" data-testid="pleo-totaal">{formatEUR(data.totaal_doorbelasten)}</p></div>
            <div><p className="text-[10px] text-[#526071] mb-1">Entiteiten</p><p className="text-sm font-bold text-[#0b1220]">{entities.length}</p></div>
            <div><p className="text-[10px] text-[#526071] mb-1">Transacties</p><p className="text-sm font-bold text-[#0b1220]">{data.transacties}</p></div>
            <div><p className="text-[10px] text-[#526071] mb-1">TW1G (overgeslagen)</p><p className="text-sm font-bold font-mono text-[#526071]">{formatEUR(data.group_totaal)}</p></div>
          </div>
          {data.warnings?.length > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-[#f59e0b]/5 border border-[#f59e0b]/20">
              <p className="text-xs font-semibold text-[#f59e0b] mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {data.warnings.length} waarschuwing{data.warnings.length > 1 ? 'en' : ''}</p>
              {data.warnings.map((w, i) => <p key={i} className="text-xs text-[#526071]">{w}</p>)}
            </div>
          )}
        </div>
      )}

      {/* STEP 1: Overzicht */}
      {step === 1 && data && (
        <div className="space-y-4">
          <div className="tw1-card overflow-hidden" data-testid="pleo-entity-table">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-sm text-[#0b1220]">Overzicht per entiteit</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 text-[#526071] text-xs">
                  <th className="text-left px-4 py-2.5 font-medium">Entiteit</th>
                  <th className="text-right px-4 py-2.5 font-medium">Regels</th>
                  <th className="text-right px-4 py-2.5 font-medium">Totaal</th>
                  <th className="text-center px-4 py-2.5 font-medium">1482</th>
                  <th className="px-4 py-2.5 w-8"></th>
                </tr></thead>
                <tbody>
                  {entities.map((ent, i) => {
                    const code1482 = get1482(ent.entity);
                    const isExpanded = expanded[ent.entity];
                    const totalRegels = Object.values(ent.periodes).reduce((s, p) => s + p.regels.length, 0);
                    return (
                      <React.Fragment key={ent.entity}>
                        <tr className={`border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer ${!code1482 ? 'bg-red-50/30' : ''}`}
                          onClick={() => setExpanded(prev => ({ ...prev, [ent.entity]: !prev[ent.entity] }))}
                          data-testid={`pleo-entity-row-${i}`}>
                          <td className="px-4 py-2.5 font-medium text-[#0b1220]">{ent.entity}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-[#526071]">{totalRegels}</td>
                          <td className="px-4 py-2.5 text-right font-mono font-medium">{formatEUR(ent.totaal)}</td>
                          <td className="px-4 py-2.5 text-center">
                            {code1482 ? <Badge className="text-[10px] bg-[#1e8c45]/10 text-[#1e8c45] hover:bg-[#1e8c45]/10">{code1482}</Badge> : <Badge variant="destructive" className="text-[10px]">Ontbreekt</Badge>}
                          </td>
                          <td className="px-4 py-2.5">{isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-[#526071]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#526071]" />}</td>
                        </tr>
                        {isExpanded && Object.entries(ent.periodes).sort(([a],[b]) => Number(a) - Number(b)).map(([pKey, pData]) => (
                          <React.Fragment key={`${ent.entity}-${pKey}`}>
                            <tr className="bg-[#003366]/[0.02]">
                              <td colSpan={2} className="px-6 py-1.5 text-xs font-semibold text-[#003366]">Periode {pKey} — {MONTH_NAMES[(Number(pKey)-1)] || pKey}</td>
                              <td className="px-4 py-1.5 text-right text-xs font-mono font-semibold text-[#003366]">{formatEUR(pData.subtotaal)}</td>
                              <td colSpan={2}></td>
                            </tr>
                            {pData.regels.map((r, ri) => (
                              <tr key={ri} className="bg-gray-50/30">
                                <td className="px-8 py-1 text-xs text-[#526071]"><span className="font-mono">{r.gb_code}</span> {r.gb_naam}</td>
                                <td></td>
                                <td className="px-4 py-1 text-right text-xs font-mono text-[#526071]">{formatEUR(r.netto)}</td>
                                <td colSpan={2}></td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {/* TW1G section */}
                  {groupEntity && (
                    <tr className="bg-gray-100/50 border-t-2 border-gray-200">
                      <td className="px-4 py-2.5 text-[#526071] italic">TW1G (Group — overgeslagen)</td>
                      <td className="px-4 py-2.5 text-right font-mono text-[#526071]">{Object.values(groupEntity.periodes).reduce((s, p) => s + p.regels.length, 0)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-[#526071]">{formatEUR(groupEntity.totaal)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  )}
                  {/* Totaal */}
                  <tr className="border-t-2 border-[#003366]/20 font-bold">
                    <td className="px-4 py-3 text-[#0b1220]">Totaal door te belasten</td>
                    <td></td>
                    <td className="px-4 py-3 text-right font-mono text-[#003366]">{formatEUR(data.totaal_doorbelasten)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Extra 1482 codes */}
          {missingCodes.length > 0 && (
            <div className="tw1-card p-5" data-testid="pleo-missing-codes">
              <h3 className="font-semibold text-sm text-[#0b1220] mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#c0392b]" /> 1482-codes ontbreken</h3>
              <p className="text-xs text-[#526071] mb-3">Vul de intercompany relatiecodes in voor deze entiteiten:</p>
              <div className="space-y-2">
                {missingCodes.map(entity => (
                  <div key={entity} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-[#0b1220] w-48">{entity}</span>
                    <Input
                      value={extraCodes[entity] || ''}
                      onChange={e => {
                        const updated = { ...extraCodes, [entity]: e.target.value };
                        setExtraCodes(updated);
                      }}
                      placeholder="bijv. 1482XXXX"
                      className="h-8 w-40 text-xs font-mono"
                      data-testid={`pleo-code-${entity.replace(/\s+/g, '-')}`}
                    />
                  </div>
                ))}
                <Button size="sm" className="text-xs h-7 bg-[#003366] hover:bg-[#00244d] text-white mt-2" onClick={() => saveExtraCodes(extraCodes)} data-testid="pleo-save-codes-btn">
                  Opslaan
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={() => setStep(2)} className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white h-9" data-testid="pleo-to-export-btn">
              <Download className="w-4 h-4 mr-1.5" />Naar Exporteren
            </Button>
          </div>
        </div>
      )}

      {/* STEP 2: Export */}
      {step === 2 && data && (
        <div className="space-y-4">
          {/* Validation */}
          <div className="tw1-card p-5" data-testid="pleo-validation">
            <h3 className="font-semibold text-sm text-[#0b1220] mb-3">Validatie</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {balanceOk ? <Check className="w-4 h-4 text-[#1e8c45]" /> : <AlertTriangle className="w-4 h-4 text-[#c0392b]" />}
                <span className="text-xs">Balanscheck: Group ({formatEUR(groupCreditTotal)}) = Holding ({formatEUR(holdingTotal)}) = Entiteiten ({formatEUR(entityDebitTotal)})</span>
                <Badge className={`text-[10px] ${balanceOk ? 'bg-[#1e8c45]/10 text-[#1e8c45]' : 'bg-[#c0392b]/10 text-[#c0392b]'}`}>{balanceOk ? 'OK' : 'VERSCHIL'}</Badge>
              </div>
              <div className="flex items-center gap-2">
                {missingCodes.length === 0 ? <Check className="w-4 h-4 text-[#1e8c45]" /> : <AlertTriangle className="w-4 h-4 text-[#c0392b]" />}
                <span className="text-xs">1482-codes: alle entiteiten hebben een code</span>
                <Badge className={`text-[10px] ${missingCodes.length === 0 ? 'bg-[#1e8c45]/10 text-[#1e8c45]' : 'bg-[#c0392b]/10 text-[#c0392b]'}`}>{missingCodes.length === 0 ? 'OK' : `${missingCodes.length} ontbreekt`}</Badge>
              </div>
            </div>
          </div>

          {/* Export preview */}
          <div className="tw1-card p-5">
            <h3 className="font-semibold text-sm text-[#0b1220] mb-3">Te genereren bestanden</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50">
                <FileText className="w-4 h-4 text-[#003366]" />
                <span className="text-xs font-medium flex-1">Pleo_IC_Group_{periodeFileLabel}_{boekjaar}.xls</span>
                <Badge variant="outline" className="text-[10px]">Dagboek 90</Badge>
              </div>
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50">
                <FileText className="w-4 h-4 text-[#003366]" />
                <span className="text-xs font-medium flex-1">Pleo_IC_Holding_{periodeFileLabel}_{boekjaar}.xls</span>
                <Badge variant="outline" className="text-[10px]">Dagboek 60</Badge>
              </div>
              {entities.filter(e => e.totaal !== 0).map(e => (
                <div key={e.entity} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50">
                  <FileText className="w-4 h-4 text-[#f59e0b]" />
                  <span className="text-xs font-medium flex-1">Pleo_IC_{e.entity.replace(/\s+/g,'_')}_{periodeFileLabel}_{boekjaar}.xls</span>
                  <Badge variant="outline" className="text-[10px]">Dagboek 90</Badge>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleExport} disabled={exporting || missingCodes.length > 0} className="tw1-btn bg-[#f59e0b] hover:bg-[#d97706] text-[#003366] h-9 px-6 font-bold" data-testid="pleo-export-btn">
              {exporting ? (<><span className="animate-spin mr-2 w-4 h-4 border-2 border-[#003366]/30 border-t-[#003366] rounded-full inline-block" />Exporteren...</>) : (<><Download className="w-4 h-4 mr-1.5" />Genereer IC Correctie bestanden</>)}
            </Button>
            <Button variant="outline" onClick={clearAll} className="h-9 text-xs"><Trash2 className="w-3.5 h-3.5 mr-1" />Opnieuw</Button>
          </div>
        </div>
      )}
    </div>
  );
}
